// Maschera provincia (port della funzione v1 wsRenderProvinceMask): quando si
// seleziona una provincia, OSCURA tutto ciò che è fuori. In MapLibre è un fill
// = "mondo" con la provincia come buco (hole), + il bordo bianco della provincia.
// Segue mappa/zoom da solo (layer geografico, niente rAF come la v1 SVG).

const SRC = 'ws-provmask';
const LYR = 'ws-provmask-fill';
const BSRC = 'ws-provbound';
const BLYR = 'ws-provbound-line';
// Ring "mondo" (Web Mercator valido fino a ~±85°): copre sempre il viewport.
const WORLD = [[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]];

let byCode = null;
let loading = null;
const maskRequests = new WeakMap();

async function loadProvinces() {
    if (byCode) return byCode;
    if (!loading) {
        loading = fetch('/v2/assets/data/sicily-provinces.geojson', { cache: 'force-cache' })
            .then(r => r.json())
            .then(gj => {
                byCode = new Map();
                (gj.features || []).forEach(f => {
                    const c = String(f.properties?.province_code || '').toUpperCase();
                    if (c) byCode.set(c, f);
                });
                return byCode;
            })
            .catch(() => { byCode = new Map(); return byCode; });
    }
    return loading;
}

// Anelli esterni della provincia → diventano i buchi del mondo.
function outerRings(feature) {
    const g = feature.geometry, out = [];
    if (!g) return out;
    if (g.type === 'Polygon') out.push(g.coordinates[0]);
    else if (g.type === 'MultiPolygon') g.coordinates.forEach(poly => out.push(poly[0]));
    return out;
}

function ringArea(ring) {
    let area = 0;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        area += (ring[j][0] * ring[i][1]) - (ring[i][0] * ring[j][1]);
    }
    return Math.abs(area / 2);
}

// Bounds della parte principale della provincia. Per AG e TP il GeoJSON include
// anche Pelagie/Pantelleria: usarle nel fit renderebbe la provincia siciliana
// minuscola, mentre quelle isole sono già mostrate negli inset dedicati.
export async function provinceMainlandBounds(code) {
    const provs = await loadProvinces();
    const f = provs.get(String(code || '').toUpperCase());
    const g = f?.geometry;
    if (!g) return null;

    let polygon;
    if (g.type === 'Polygon') polygon = g.coordinates;
    else if (g.type === 'MultiPolygon') {
        polygon = g.coordinates.reduce((largest, candidate) =>
            ringArea(candidate[0] || []) > ringArea(largest?.[0] || []) ? candidate : largest, null);
    }
    const ring = polygon?.[0];
    if (!ring?.length) return null;

    let west = Infinity, south = Infinity, east = -Infinity, north = -Infinity;
    for (const point of ring) {
        west = Math.min(west, point[0]);
        south = Math.min(south, point[1]);
        east = Math.max(east, point[0]);
        north = Math.max(north, point[1]);
    }
    return [[west, south], [east, north]];
}

export function clearProvinceMask(map) {
    if (!map) return;
    maskRequests.set(map, (maskRequests.get(map) || 0) + 1);
    if (!map.getStyle?.()) return;
    for (const l of [BLYR, LYR]) { if (map.getLayer(l)) map.removeLayer(l); }
    for (const s of [BSRC, SRC]) { if (map.getSource(s)) map.removeSource(s); }
}

export async function setProvinceMask(map, code) {
    if (!map) return;
    clearProvinceMask(map);
    const request = maskRequests.get(map);
    if (!code) return;
    const provs = await loadProvinces();
    if (maskRequests.get(map) !== request || !map.getStyle?.()) return;
    const f = provs.get(String(code).toUpperCase());
    if (!f) return;
    const holes = outerRings(f);
    if (!holes.length) return;
    const mask = { type: 'Feature', geometry: { type: 'Polygon', coordinates: [WORLD, ...holes] } };
    // In cima allo stack → oscura base + marker fuori provincia; il buco lascia
    // vedere tutto dentro. Il bordo va sopra la maschera.
    map.addSource(SRC, { type: 'geojson', data: mask });
    map.addLayer({ id: LYR, type: 'fill', source: SRC, paint: { 'fill-color': '#0a1826', 'fill-opacity': 0.58 } });
    map.addSource(BSRC, { type: 'geojson', data: f });
    map.addLayer({ id: BLYR, type: 'line', source: BSRC, paint: { 'line-color': '#ffffff', 'line-width': 1.6, 'line-opacity': 0.85 } });
    // I marker (stazioni + previsioni, con aloni) devono restare SOPRA la maschera:
    // le stazioni/previsioni fuori provincia sono già filtrate via, ma una card a
    // cavallo del confine finirebbe sotto la parte opaca del ritaglio. Portiamo i
    // layer puntuali in cima, sopra fill e bordo.
    for (const id of ['ws-stations-rain-halo', 'ws-stations-symbols', 'ws-forecast-symbols']) {
        if (map.getLayer(id)) { try { map.moveLayer(id); } catch (e) {} }
    }
}
